import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import {
  buildExitClicksView,
  type ExitClicksPeriod,
  type ExitClicksView,
} from './exitClicksModel';
import type { Click, Merchant, MerchantRegion } from '../dataProvider/types';

/**
 * Region-driven Exit clicks view, read through the aggregation seam
 * (`buildExitClicksView`), NOT computed in the component. For v1 it pulls the raw
 * clicks, merchants and merchant regions from the mock provider and aggregates
 * client-side; the real-data migration replaces the fetch-and-aggregate here with
 * a single server-side endpoint and the surface does not change. Same seam idiom
 * as useStorePerformance.
 *
 * The aggregator deliberately does NOT fetch transactions: the leak count comes
 * from the click's `converted` boolean, never the transactions.clickId join.
 *
 * Scope comes from the region context (UK | US | GLOBAL): the surface shows every
 * band the scope derives to at once, so it keys off `region`, not `activeRegion`.
 * `now` is injected so the period window and the maturation cutoff are
 * deterministic under test.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useExitClicks = (opts: {
  period: ExitClicksPeriod;
  now: number;
}): { view: ExitClicksView | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();
  const { region } = useRegion();

  const { data, isLoading } = useQuery({
    queryKey: ['exitClicks', region, opts.period, opts.now],
    queryFn: async () => {
      const [clicks, merchants, merchantRegions] = await Promise.all([
        dataProvider.getList('clicks', ALL),
        dataProvider.getList('merchants', ALL),
        dataProvider.getList('merchantRegions', ALL),
      ]);
      return buildExitClicksView(
        {
          clicks: clicks.data as unknown as Click[],
          merchants: merchants.data as unknown as Merchant[],
          merchantRegions: merchantRegions.data as unknown as MerchantRegion[],
        },
        { scope: region, period: opts.period, now: opts.now },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
